// canvas/hidpi.js: crisp game canvases on high-density screens.
//
// A canvas with one backing pixel per CSS pixel is stretched by the
// browser on a 2x or 3x screen, which smooths the pixel-art cards. The
// game canvases instead keep a backing store of round(CSS size x
// devicePixelRatio) and scale the context by the ratio, so every
// renderer keeps drawing in CSS px (layout numbers, hit zones and pointer
// handlers stay in CSS px) while the art lands on device pixels.
//
// No image-rendering: pixelated here: it would also make canvas text
// blocky.

/**
 * The current devicePixelRatio, validated.
 *
 * @param {{ devicePixelRatio?: number }} [win]
 * @returns {number}
 */
export function readDevicePixelRatio(win = globalThis.window) {
  const dpr = win ? win.devicePixelRatio : undefined;
  if (dpr === undefined) return 1; // no window (Node): CSS px are device px
  if (!Number.isFinite(dpr) || dpr <= 0) {
    throw new Error(`readDevicePixelRatio: bad devicePixelRatio ${dpr}`);
  }
  return dpr;
}

/**
 * Backing-store size for a canvas of cssW x cssH CSS px at ratio dpr.
 *
 * @param {number} cssW
 * @param {number} cssH
 * @param {number} dpr
 * @returns {{ width: number, height: number }}
 */
export function backingSize(cssW, cssH, dpr) {
  return { width: Math.round(cssW * dpr), height: Math.round(cssH * dpr) };
}

// A stage this close to a device pixel (device px) counts as on it. Box
// reads carry float noise of about 1e-4 device px. Layout moves boxes in
// 1/64 CSS px steps, which leaves a stage at least 1/512 device px off a
// pixel at the ratios in use (1.25, 2, 2.625, 2.75, 3), so a real miss is
// never taken for noise.
const ON_PIXEL = 1e-3;

/** Move a canvas and its hit targets together so its origin is a device pixel. */
const lastShift = new WeakMap();
// "x" or "x y" in px (a browser drops a zero y when it reads the
// translate back); null for any other form.
function parseShift(text) {
  if (text === undefined || text === null || text === '' || text === 'none') return [0, 0];
  const parts = String(text).trim().split(/\s+/);
  if (parts.length > 2 || !parts.every((p) => /^-?[\d.]+(e-?\d+)?px$/.test(p))) return null;
  const [x, y = 0] = parts.map(parseFloat);
  return [x, y];
}
// Write the shift and keep it with the text the browser reads back: the
// browser keeps its own text form (Chrome: 6 significant digits, no zero
// y), so the written text would never match the next read.
function writeShift(stage, dx, dy) {
  stage.style.translate = `${dx}px ${dy}px`;
  lastShift.set(stage, { dx, dy, text: stage.style.translate });
}
export function alignCanvasStage(stage, dpr) {
  // An aligned stage is left alone: no restyle, so checking is cheap and
  // cannot start a loop.
  const now = stage.getBoundingClientRect();
  const off = (v) => Math.abs(v * dpr - Math.round(v * dpr));
  if (off(now.left) < ON_PIXEL && off(now.top) < ON_PIXEL) return;
  // One box read per check: the unshifted box is the read box minus the
  // translate it carries now, so resize and ratio changes start from the
  // true spot and old corrections cannot pile up. CSS translate moves
  // children and their pointer targets together without changing the
  // flex layout. The check's own last write is known exactly (the
  // browser reads it back rounded); any other translate in px, one or
  // two values from any writer, is read from its text.
  const cur = stage.style.translate;
  const rec = lastShift.get(stage);
  const shift = rec && rec.text === cur ? [rec.dx, rec.dy] : parseShift(cur);
  if (!shift) {
    // A translate that cannot be read as px (other units, calc()): its
    // size in px is unknown, so clear it and read the unshifted box
    // again. This is the only correction that reads twice.
    stage.style.translate = 'none';
    const box = stage.getBoundingClientRect();
    writeShift(stage, (Math.round(box.left * dpr) - box.left * dpr) / dpr, (Math.round(box.top * dpr) - box.top * dpr) / dpr);
    return;
  }
  const baseLeft = now.left - shift[0];
  const baseTop = now.top - shift[1];
  writeShift(stage, (Math.round(baseLeft * dpr) - baseLeft * dpr) / dpr, (Math.round(baseTop * dpr) - baseTop * dpr) / dpr);
}

// Events that can move the stage without a DOM change, caught on the
// document from every element (scroll does not bubble, so capture).
const DOC_EVENTS = ['scroll', 'load', 'transitionend', 'animationend'];

/**
 * Keep a canvas stage on a whole device pixel, whatever moves it. Returns
 * a function that stops watching.
 *
 * The stage can only move when the page changes: a DOM change anywhere
 * (a banner, a dialog, a neighbour's text or style, the stage's own
 * style other than the check's translate), a scroll, a window
 * resize, a font or image load, or the end of a CSS transition or
 * animation. Each of those schedules one check, run just after the next
 * frame is drawn; the check reads the stage's box and fixes it if it is
 * off a device pixel. The check does not care how far the stage moved or
 * how much of it shows. With no change, nothing runs.
 *
 * After the frame, layout is already up to date, so the box read costs
 * almost nothing. Read before the frame, right after a DOM change, it
 * would force the browser to lay out the page early (up to 5 ms at 4x CPU
 * throttling). The price: a moved stage is fixed one frame later.
 *
 * Limits: a layout change with none of those causes (a stylesheet rule
 * edited through the CSSOM, a :hover rule that changes size) is fixed at
 * the next change of any kind. The app does neither. While a CSS
 * transition runs, the stage is fixed when it ends.
 *
 * An element marked data-paint-only (the turn bar, whose transform its
 * own component sets every frame) cannot move anything: changes to its
 * style schedule no check.
 *
 * @param {HTMLElement} stage
 * @param {number} dpr
 * @returns {() => void}
 */
export function keepStageAligned(stage, dpr) {
  const doc = stage.ownerDocument;
  const win = doc.defaultView;
  if (!win) throw new Error('keepStageAligned: stage is not in a window');
  let frame = 0;
  let pending = false;
  let stopped = false;

  // The stage's style as the last check left it. A style change on the
  // stage that leaves this text as it was is the check's own translate
  // write; any other (its top, margin, size) can move it.
  let checkedStyle = '';
  const check = () => {
    alignCanvasStage(stage, dpr);
    checkedStyle = stage.style.cssText;
  };
  const alignStageFrame = () => {
    pending = false;
    if (!stopped) check();
  };
  // A task posted from an animation frame callback runs after that frame
  // is drawn. At user-blocking priority it also runs before the app's
  // own after-frame work (Preact runs effects, such as canvas draws, in a
  // timer task then), which would change the DOM and make the read pay
  // for a layout. Browsers without scheduler.postTask (Safari) get a
  // posted message: the check is the same, but it can land after that
  // work and then pays for the layout itself.
  const scheduler = /** @type {any} */ (win).scheduler;
  /** @type {MessageChannel | null} */
  let channel = null;
  if (!scheduler?.postTask) {
    channel = new win.MessageChannel();
    channel.port1.onmessage = alignStageFrame;
  }
  const postAfterFrame = () => {
    frame = 0;
    if (channel) channel.port2.postMessage(null);
    else scheduler.postTask(alignStageFrame, { priority: 'user-blocking' });
  };
  const schedule = () => {
    if (pending) return;
    pending = true;
    frame = win.requestAnimationFrame(postAfterFrame);
  };
  const mutations = new win.MutationObserver((records) => {
    const ownWrite = (r) => r.target === stage && r.attributeName === 'style'
      && stage.style.cssText === checkedStyle;
    const paintOnly = (r) => r.attributeName === 'style' && r.target.nodeType === 1
      && /** @type {Element} */ (r.target).hasAttribute('data-paint-only');
    if (records.some((r) => !ownWrite(r) && !paintOnly(r))) schedule();
  });

  check();
  mutations.observe(doc.documentElement, { subtree: true, childList: true, attributes: true, characterData: true });
  for (const type of DOC_EVENTS) doc.addEventListener(type, schedule, { capture: true, passive: true });
  win.addEventListener('resize', schedule);
  doc.fonts.addEventListener('loadingdone', schedule);
  return () => {
    stopped = true;
    if (frame) win.cancelAnimationFrame(frame);
    frame = 0;
    if (channel) channel.port1.close();
    mutations.disconnect();
    for (const type of DOC_EVENTS) doc.removeEventListener(type, schedule, { capture: true });
    win.removeEventListener('resize', schedule);
    doc.fonts.removeEventListener('loadingdone', schedule);
  };
}

/**
 * Size a canvas's backing store for the ratio and scale its context so
 * renderers draw in CSS px. Safe to call before every frame: it only
 * resizes (which clears the canvas) when the size changed, and it always
 * resets the transform, so a renderer that left a transform behind
 * cannot shift the next frame.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {number} cssW
 * @param {number} cssH
 * @param {number} dpr
 * @returns {CanvasRenderingContext2D | null}
 */
export function prepareHiDpiCanvas(canvas, cssW, cssH, dpr) {
  const { width, height } = backingSize(cssW, cssH, dpr);
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

/**
 * Call onChange(newRatio) whenever devicePixelRatio changes (browser zoom,
 * or the window moving to a screen with another density). Uses a
 * resolution media query for the current ratio and re-arms it after each
 * change. A window resize also re-reads the ratio: a browser zoom resizes
 * the window, and headless Chrome under ratio emulation changes the ratio
 * without firing the query. Each change is reported once. Returns a
 * function that stops watching.
 *
 * @param {(dpr: number) => void} onChange
 * @param {Window} [win]
 * @returns {() => void}
 */
export function watchDevicePixelRatio(onChange, win = window) {
  if (typeof win.matchMedia !== 'function') {
    throw new Error('watchDevicePixelRatio: window.matchMedia is missing');
  }
  /** @type {MediaQueryList | null} */
  let mql = null;
  let stopped = false;
  let last = readDevicePixelRatio(win);
  const handler = () => {
    if (stopped) return;
    const now = readDevicePixelRatio(win);
    if (now === last) return;
    last = now;
    arm();
    onChange(now);
  };
  function arm() {
    if (mql) mql.removeEventListener('change', handler);
    mql = win.matchMedia(`(resolution: ${last}dppx)`);
    mql.addEventListener('change', handler);
  }
  arm();
  win.addEventListener('resize', handler);
  return () => {
    stopped = true;
    if (mql) mql.removeEventListener('change', handler);
    win.removeEventListener('resize', handler);
  };
}
